<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Administración de Negocios
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Gestiona todos los negocios del sistema
      </p>
    </div>
    
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Lista de Negocios
          </h2>
          <div class="flex gap-2">
            <button @click="goToUsuarios" class="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
              </svg>
              Gestionar Usuarios
            </button>
            <button @click="showModal = true" class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Nuevo Negocio
            </button>
          </div>
        </div>
        
        <!-- Filtros -->
        <form @submit.prevent="onFilter" class="flex flex-wrap gap-2 mt-4">
          <input v-model="filters.nombre" type="text" placeholder="Nombre" class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          <button type="submit" class="px-4 py-2 rounded bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition">Buscar</button>
          <button type="button" @click="onClearFilters" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Limpiar</button>
        </form>
      </div>
      
      <div class="p-6">
        <div v-if="error" class="text-red-500 text-center mb-4">{{ error }}</div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-4 py-2 font-bold">ID</th>
                <th class="px-4 py-2 font-bold">Nombre</th>
                <th class="px-4 py-2 font-bold">Estado</th>
                <th class="px-4 py-2 font-bold">Porcentajes</th>
                <th class="px-4 py-2 font-bold">Usuarios</th>
                <th class="px-4 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="negocio in negocios" :key="negocio.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 even:bg-gray-50 dark:even:bg-gray-700">
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">{{ negocio.id }}</td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ negocio.nombre }}</td>
                <td class="px-4 py-2">
                  <span v-if="negocio.activo" class="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-green-900 dark:text-green-300">Activo</span>
                  <span v-else class="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">Inactivo</span>
                </td>
                <td class="px-4 py-2">
                  <div class="flex flex-wrap gap-1">
                    <span class="bg-blue-100 text-blue-800 text-xs font-medium px-1.5 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">
                      P1: {{ negocio.porcentaje1 || '25.00' }}%
                    </span>
                    <span class="bg-green-100 text-green-800 text-xs font-medium px-1.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                      P2: {{ negocio.porcentaje2 || '35.00' }}%
                    </span>
                    <span class="bg-yellow-100 text-yellow-800 text-xs font-medium px-1.5 py-0.5 rounded dark:bg-yellow-900 dark:text-yellow-300">
                      P3: {{ negocio.porcentaje3 || '50.00' }}%
                    </span>
                    <span class="bg-purple-100 text-purple-800 text-xs font-medium px-1.5 py-0.5 rounded dark:bg-purple-900 dark:text-purple-300">
                      P4: {{ negocio.porcentaje4 || '75.00' }}%
                    </span>
                  </div>
                </td>
                <td class="px-4 py-2 text-center">
                  <button
                    @click="openUsersModal(negocio)"
                    class="relative text-blue-600 hover:text-blue-800 p-2 rounded transition"
                    :title="`Ver usuarios de ${negocio.nombre}`"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                    </svg>
                    <span
                      v-if="negocio.users_count"
                      class="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full border-2 border-white dark:border-gray-900"
                      style="min-width: 1.5em; text-align: center;"
                    >
                      {{ negocio.users_count }}
                    </span>
                  </button>
                </td>
                <td class="px-4 py-2 whitespace-nowrap text-right">
                  <button @click="openEditModal(negocio)" class="text-blue-500 hover:text-blue-700 mr-2" title="Editar">
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536M9 13h3l8-8a2.828 2.828 0 00-4-4l-8 8v3z"/>
                    </svg>
                  </button>
                  <button @click="onDeleteNegocio(negocio)" class="text-red-500 hover:text-red-700" title="Eliminar">
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="negocios.length === 0">
                <td colspan="6" class="text-center py-4 text-gray-400">No hay negocios para mostrar.</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- Paginador -->
        <Pagination 
          :current-page="currentPage" 
          :last-page="lastPage" 
          @page-changed="goToPage" 
        />
      </div>
    </div>
    
    <!-- Modal para Nuevo/Editar Negocio -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ editMode ? 'Editar Negocio' : 'Nuevo Negocio' }}
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <form @submit.prevent="editMode ? updateNegocio() : createNegocio()" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
            <input v-model="negocioForm.nombre" type="text" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white" placeholder="Ingrese el nombre del negocio" />
          </div>
          <div>
            <label class="flex items-center">
              <input v-model="negocioForm.activo" type="checkbox" class="rounded border-gray-300 text-violet-600 shadow-sm focus:ring-violet-500 focus:ring-offset-0" />
              <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Activo</span>
            </label>
          </div>

          <!-- Porcentajes de ganancia -->
          <div class="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
            <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Porcentajes de Ganancia</h4>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">Define los porcentajes de ganancia que se aplicarán a los prototipos creados por este negocio</p>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Precio 1 (%)</label>
                <input
                  v-model.number="negocioForm.porcentaje1"
                  type="number"
                  step="0.01"
                  min="0"
                  max="999.99"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="25.00"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Precio 2 (%)</label>
                <input
                  v-model.number="negocioForm.porcentaje2"
                  type="number"
                  step="0.01"
                  min="0"
                  max="999.99"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="35.00"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Precio 3 (%)</label>
                <input
                  v-model.number="negocioForm.porcentaje3"
                  type="number"
                  step="0.01"
                  min="0"
                  max="999.99"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="50.00"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Precio 4 (%)</label>
                <input
                  v-model.number="negocioForm.porcentaje4"
                  type="number"
                  step="0.01"
                  min="0"
                  max="999.99"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="75.00"
                />
              </div>
            </div>
          </div>
          <div class="flex justify-end space-x-3 pt-4">
            <button type="button" @click="closeModal" class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancelar</button>
            <button type="submit" :disabled="loading" class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
              <svg v-if="loading" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ loading ? (editMode ? 'Actualizando...' : 'Creando...') : (editMode ? 'Actualizar' : 'Crear') }}
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Modal de Usuarios del Negocio -->
    <div v-if="showUsersModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Usuarios de {{ selectedNegocio?.nombre }}
            </h3>
            <button @click="closeUsersModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="p-6">
          <div v-if="selectedNegocio?.users && selectedNegocio.users.length">
            <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
                <tr>
                  <th class="px-4 py-2 font-bold">Nombre</th>
                  <th class="px-4 py-2 font-bold">Email</th>
                  <th class="px-4 py-2 font-bold">Rol</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in selectedNegocio.users" :key="user.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                  <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ user.nombre + ' ' + user.apellidoPaterno + ' ' + user.apellidoMaterno }}</td>
                  <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ user.email }}</td>
                  <td class="px-4 py-2">
                    <RolUsuario 
                      :rol-id="user.rol?.id || 0"
                      :nombre="user.rol?.nombre || 'Sin rol'"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-gray-400 text-center py-8">No hay usuarios asignados a este negocio.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useHttp } from '@/composables/useHttp'
import RolUsuario from '@/components/RolUsuario.vue'
import Swal from 'sweetalert2'
import Pagination from '@/components/Pagination.vue'

const router = useRouter()

const negocios = ref([])
const currentPage = ref(1)
const lastPage = ref(1)
const perPage = 12
const showModal = ref(false)
const showUsersModal = ref(false)
const editMode = ref(false)
const selectedNegocio = ref(null)
const negocioForm = ref({
  id: null,
  nombre: '',
  activo: true,
  porcentaje1: 25.00,
  porcentaje2: 35.00,
  porcentaje3: 50.00,
  porcentaje4: 75.00
})
const filters = ref({ nombre: '' })
const { get, post, del, loading, error, data } = useHttp()

const fetchNegocios = async (page = 1) => {
  try {
    const params = new URLSearchParams()
    params.append('page', page)
    params.append('per_page', perPage)
    if (filters.value.nombre) params.append('nombre', filters.value.nombre)
    
    await get(`negocios?${params.toString()}`)
    if (data.value) {
      if (Array.isArray(data.value)) {
        negocios.value = data.value
        currentPage.value = 1
        lastPage.value = 1
      } else {
        negocios.value = data.value.data
        currentPage.value = data.value.current_page
        lastPage.value = data.value.last_page
      }
    }
  } catch (err) {
    negocios.value = []
  }
}

const goToPage = (page) => {
  if (page >= 1 && page <= lastPage.value) fetchNegocios(page)
}

const onFilter = () => { fetchNegocios(1) }
const onClearFilters = () => { 
  filters.value = { nombre: '' }
  fetchNegocios(1) 
}

const closeModal = () => {
  showModal.value = false
  editMode.value = false
  negocioForm.value = {
    id: null,
    nombre: '',
    activo: true,
    porcentaje1: 25.00,
    porcentaje2: 35.00,
    porcentaje3: 50.00,
    porcentaje4: 75.00
  }
}

const openEditModal = (negocio) => {
  negocioForm.value = {
    ...negocio,
    activo: Boolean(negocio.activo),
    porcentaje1: negocio.porcentaje1 || 25.00,
    porcentaje2: negocio.porcentaje2 || 35.00,
    porcentaje3: negocio.porcentaje3 || 50.00,
    porcentaje4: negocio.porcentaje4 || 75.00
  }
  showModal.value = true
  editMode.value = true
}

const openUsersModal = async (negocio) => {
  try {
    await get(`negocios/${negocio.id}/usuarios`)
    console.log("result de openUsersModal: ", data.value);
    if (data.value) {
      selectedNegocio.value = { ...negocio, users: data.value.data }
      showUsersModal.value = true
    }
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar los usuarios del negocio' })
  }
}

const closeUsersModal = () => {
  showUsersModal.value = false
  selectedNegocio.value = null
}

const createNegocio = async () => {
  try {
    // Convert boolean back to number for backend
    const payload = {
      ...negocioForm.value,
      activo: negocioForm.value.activo ? 1 : 0
    }
    await post('negocios', payload)
    closeModal()
    await fetchNegocios(currentPage.value)
    Swal.fire({ icon: 'success', title: 'Negocio creado', text: 'El negocio se creó exitosamente.', timer: 2000, showConfirmButton: false })
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.value || 'Error desconocido al crear negocio' })
  }
}

const updateNegocio = async () => {
  try {
    const payload = {
      ...negocioForm.value,
      activo: negocioForm.value.activo ? 1 : 0
    }
    console.log("Payload updateNegocio: ", payload);
    await post(`negocios/${negocioForm.value.id}?_method=PUT`, payload)
    closeModal()
    await fetchNegocios(currentPage.value)
    Swal.fire({ icon: 'success', title: 'Negocio actualizado', text: 'El negocio se actualizó exitosamente.', timer: 2000, showConfirmButton: false })
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.value || 'Error desconocido al actualizar negocio' })
  }
}

const onDeleteNegocio = async (negocio) => {
  const result = await Swal.fire({
    title: `¿Eliminar negocio?`,
    text: `¿Seguro que deseas eliminar "${negocio.nombre}"? Esta acción no se puede deshacer.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await del(`negocios/${negocio.id}`)
      await Swal.fire({ title: '¡Eliminado!', text: 'El negocio ha sido eliminado correctamente.', icon: 'success', timer: 1800, showConfirmButton: false })
      fetchNegocios(currentPage.value)
    } catch (err) {
      Swal.fire('Error', 'No se pudo eliminar el negocio.', 'error')
    }
  }
}

const goToUsuarios = () => {
  router.push('/users')
}


onMounted(() => {
  fetchNegocios()
})
</script>